import { Button, CircularProgress, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip } from '@material-ui/core'

import { makeStyles } from '@material-ui/core/styles'
import * as Common from '@tfo/common'
import { Elements, ExpandingSection, ReportTable, SearchInput, TwoColRow } from '@tfo/common'
import { DetailedArchivedReport } from '@tfo/common/src/interfaces'
import classnames from 'classnames'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useHistory, useParams } from 'react-router-dom'
import ExcelIcon from '../../../../components/icons/ExcelIcon'
import bkflogo from '../../../../media/images/common/BKF-standard-b.png'
import logo from '../../../../media/images/tfo-logo.svg'
import $http from '../../../../utils/$http'
import ArchivedCheckpoints from './ArchivedCheckpoints'
import ExcelExportStatus from './ExcelExportStatus'
import { downloadAsExcel } from './useArchiveExcelExport'

const { Heading4, Paragraph } = Elements

interface IArchivedGroupOrderResponse {
    individualInfo: IArchivedGroupOrderRow[]
    orderSpecification: IArchivedOrderSpecification
}
interface IArchivedOrderSpecification {
    reportCount: number
    reportWithDiscrepancyCount: number
    summary: string
    createdUtc: Date
    approvedUtc?: Date
    deliveredUtc?: Date
    companyId: number
    companyName: string
    ordererUserId: number
    ordererFullName: string
    reportPackageId: number
    reportPackageName: string
    reportRecipients: IReportRecipient[]
    checkpoints: ICheckpoint[]
}

interface IReportRecipient {
    userId: number
    fullName: string
}

interface ICheckpoint {
    type: string
    category: string
}

interface IArchivedGroupOrderRow {
    controlPerson: string
    individualReference?: string
    orderId: string
}

const useStyles = makeStyles({
    container: {
        padding: '2.5rem',
        "@media (min-width: 768px)": {
            padding: '0 188px'
        }
    },
    headerSpacing: {
        marginBottom: '0.5em'
    },
    contentPadding: {
        display: 'grid',
        gridTemplateColumns: '6fr 0.5fr',
        paddingTop: '1em',
        paddingBottom: '1em'
    },
    topMargin: {
        marginTop: '2rem'
    },
    searchAndFilter: {
        display: 'grid',
        gridTemplateColumns: '6fr 2fr 2fr 2fr 1fr',
        marginBottom: '30px',
        marginTop: '8px',
        "@media (max-width: 768px)": {
            display: 'grid',
            gridTemplateColumns: '6fr 4fr 4fr',
        },
    },
    archivedOrdersTableCell: {
        '&:hover': { cursor: 'pointer' }
    },
    excelBtn: {
        width: '8px',
        height: '50px',
        justifySelf: 'end',
        "@media (max-width: 799px)": {
            marginTop: '30px',
        },
    },
    reportTableContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: '3rem'
    },
    h4: {
        letterSpacing: '0.3px',
        marginLeft: '20px',
        fontWeight: 'lighter',
    },
    printContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    printDisclaimer: {
        fontStyle: 'italic',
        marginBottom: '1rem',
        '@media print': {
            marginBottom: '15rem'
        }
    },
    printButton: {
        alignSelf: 'flex-end',
        width: 'fit-content',
        marginTop: '1rem',
        marginBottom: '1rem'
    },
    excludeWhenPrinting: {
        '@media print': {
            display: 'none'
        }
    },
    logoForPrint: {
        height: '3.08rem',
        display: 'none',
        '@media print': {
            display: 'block'
        }
    }
})

const { Loader } = Common
const ArchivedGroupOrder = () => {
    const { groupOrderId } = useParams<{ groupOrderId: string }>()
    const history = useHistory()
    const intl = useIntl()
    const store = Common.hooks.useStore()
    const style = useStyles()

    const { clientViewStore } = store

    const intialPage: number = 0
    const rowsPerPage = 25

    const [currentPageIndex, setCurrentPageIndex] = React.useState<number>(intialPage)
    const [searchString, setSearchString] = React.useState<string>("")
    const [rows, setRows] = React.useState<IArchivedGroupOrderRow[]>([])
    const [orderSpecification, setOrderSpecification] = React.useState<IArchivedOrderSpecification | null>(null)
    const [filteredRows, setFilteredRows] = React.useState<IArchivedGroupOrderRow[]>([])
    const [isLoading, setIsLoading] = React.useState<boolean>(false)
    const [totalRowCount, setTotalRowCount] = React.useState<number>(0)
    const [reportName, setReportName] = React.useState<string>('')
    const [fallbackOrderInfo, setFallbackOrderInfo] = React.useState<DetailedArchivedReport | null>(null)
    const [isExporting, setIsExporting] = React.useState<boolean>(false)
    const [exportError, setExportError] = React.useState<boolean>(false)

    const { Label, Image } = Elements

    const companyNames = new Set<string>()
    companyNames.add(clientViewStore.company.name)

    React.useEffect(() => {
        setIsLoading(() => true)
    }, [])

    React.useEffect(() => {
        const timerId = setTimeout(() => {
            getArchivedGroupReports()
        }, 200)

        return () => {
            clearTimeout(timerId)
        }
    }, [])

    const getArchivedGroupReports = (): void => {
        $http.get<IArchivedGroupOrderResponse>(`/ReportArchive/${groupOrderId}`).then((response) => {
            if (!response.orderSpecification) {
                getArchivedReportName(response.individualInfo[0].orderId)
            }

            setOrderSpecification(() => response.orderSpecification)
            setRows(() => response.individualInfo)

            setRowsForPage(response.individualInfo, currentPageIndex, searchString)
            setTotalRowCount(response.individualInfo.length)

            setTimeout(() => {
                setIsLoading(() => false)
            }, 1000)
        }).catch((errorMessage) => {
            setRows(() => [])
            console.error(errorMessage)
        })
    }

    const getArchivedReportName = (orderId: string): void => {
        $http.get<DetailedArchivedReport>(`/ReportArchive/${orderId}`).then((response) => {
            setReportName(response.reportPackageName)
            setFallbackOrderInfo(response)
        }).catch((errorMessage) => {
            console.error(errorMessage)
        })
    }

    const printArchivedReport = (): void => {
        window.print()
    }

    const filterRowsByPagination = (rows: IArchivedGroupOrderRow[], pageIndex: number): IArchivedGroupOrderRow[] => {
        return rows.slice(pageIndex * rowsPerPage, pageIndex * rowsPerPage + rowsPerPage)
    }

    const filterBySearch = (rows: IArchivedGroupOrderRow[], query: string): IArchivedGroupOrderRow[] => {
        return query !== '' ? rows.filter(
            (item) =>
                item.controlPerson.toLowerCase().includes(query)
                || (item.individualReference?.toLowerCase().includes(query) ?? false)
        ) : rows
    }

    const setRowsForPage = (rows: IArchivedGroupOrderRow[], pageIndex: number, query: string) => {
        const filteredBySearchString = filterBySearch(rows, query)
        const pagedRows = filterRowsByPagination(filteredBySearchString, pageIndex)
        setFilteredRows(pagedRows)
    }

    const onChangePage = (event: unknown, pageIndex: number): void => {
        setCurrentPageIndex(pageIndex)
        setRowsForPage(rows, pageIndex, searchString)
    }

    const onClearSearch = (): void => {
        setSearchString(() => '')
        setCurrentPageIndex(0)
        setRowsForPage(rows, 0, '')
        setTotalRowCount(rows.length)
    }

    const onInputSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const query = e.target.value.toLowerCase()

        setRowsForPage(rows, 0, query)
        setTotalRowCount(filterBySearch(rows, query).length)
        setCurrentPageIndex(0)
        setSearchString(() => query)
    }

    const handleOnRowClick = (row: IArchivedGroupOrderRow): void => {
        history.push(`/archived-report/${row.orderId}`)
    }

    const exportToExcel = async (): Promise<void> => {
        if (isExporting) return
        setIsExporting(true)
        setExportError(false)
        // Let the disabled/spinner state render before doing the (synchronous) export work.
        await new Promise((resolve) => setTimeout(resolve, 0))
        try {
            buildAndDownloadExcel()
        } catch (errorMessage) {
            console.error(errorMessage)
            setExportError(true)
        } finally {
            setIsExporting(false)
        }
    }

    const buildAndDownloadExcel = (): void => {
        // Order-level info comes from the order specification when the backend provides one,
        // otherwise from the detailed report fetched as fallback for older group orders.
        const orderInfo = orderSpecification
            ? {
                companyName: orderSpecification.companyName,
                reportPackage: orderSpecification.reportPackageName,
                orderer: orderSpecification.ordererFullName,
                reportCount: orderSpecification.reportCount,
                ordered: orderSpecification.createdUtc,
                delivered: orderSpecification.deliveredUtc,
                reportWithDiscrepancy: `${orderSpecification.reportWithDiscrepancyCount}/${orderSpecification.reportCount}`,
            }
            : {
                companyName: fallbackOrderInfo?.companyName ?? '',
                reportPackage: fallbackOrderInfo?.reportPackageName ?? reportName,
                orderer: fallbackOrderInfo?.creatorName ?? '',
                reportCount: rows.length,
                ordered: fallbackOrderInfo?.created,
                delivered: fallbackOrderInfo?.delivered,
                reportWithDiscrepancy: '',
            }
        const mappedRows = filterBySearch(rows, searchString).map((row) => ({
            [intl.formatMessage({ id: 'common.company' })]: orderInfo.companyName ?? '',
            [intl.formatMessage({ id: 'common.reportPackage' })]: orderInfo.reportPackage ?? '',
            [intl.formatMessage({ id: 'common.creator' })]: orderInfo.orderer ?? '',
            [intl.formatMessage({ id: 'search.individualId' })]: row.controlPerson,
            [intl.formatMessage({ id: 'common.individualReference' })]: row.individualReference ?? '',
            [intl.formatMessage({ id: 'common.ordered' })]: orderInfo.ordered ? intl.formatDate(orderInfo.ordered) : '',
            [intl.formatMessage({ id: 'common.delivered' })]: orderInfo.delivered ? intl.formatDate(orderInfo.delivered) : '-',
            [intl.formatMessage({ id: 'common.reportWithDiscrepancy' })]: orderInfo.reportWithDiscrepancy,
        }))
        const orderedDate = orderInfo.ordered ? new Date(orderInfo.ordered).toISOString().slice(0, 10) : ''
        downloadAsExcel(mappedRows, ['Gruppbeställning', orderedDate].filter(Boolean).join(' '))
    }

    return (
        <>
            <div className={'ClientContainer' + (isLoading ? ('--loading') : '')}>
                {
                    isLoading ? <Loader></Loader> : <Grid
                        container
                        justifyContent="center"
                        direction="column"
                        className={['GridWrapper', style.topMargin].join(' ')}>
                        <div
                            className={
                                classnames(
                                    'Reports',
                                    {
                                        'Reports--empty': rows.length <= 0 && !isLoading
                                    },
                                )}>
                            {/* Logo is only displayed for printing the group order specification */}
                            <Image
                                className={style.logoForPrint}
                                src={logo}
                            />
                            <Grid className={[style.container, 'container-fluid'].join(' ')}>
                                <Grid container direction="row" justifyContent="space-between">
                                    {orderSpecification &&
                                        <div className={style.printContainer}>
                                            <Paragraph className={style.printDisclaimer}>
                                                {intl.formatMessage({ id: 'archived.report.disclaimer' })}
                                            </Paragraph>
                                            <Common.PrintButton className={`${style.excludeWhenPrinting} ${style.printButton}`} text={intl.formatMessage({ id: 'common.print' })} onClick={() => printArchivedReport()} />
                                        </div>
                                    }
                                    {reportName && <div className={style.contentPadding}>
                                        <h1 className='Heading1'>{reportName}</h1>
                                    </div>}
                                    <ArchivedGroupOrderSpecification specification={orderSpecification} />
                                </Grid>
                                <Grid container className={`${style.reportTableContainer} ${style.excludeWhenPrinting} `}>
                                    <Heading4 className={style.headerSpacing}>
                                        {intl.formatMessage({ id: 'archived.report.label.individuals' })}
                                    </Heading4>
                                    <div className={style.searchAndFilter}>

                                        <SearchInput
                                            value={searchString}
                                            onInputChange={onInputSearch}
                                            onClear={onClearSearch}
                                            placeholder={intl.formatMessage({ id: 'search.individualId' })}
                                        />
                                        <h4 className={style.h4}>{intl.formatMessage({ id: 'found.items' }) + ' '}<b>{totalRowCount}</b></h4>
                                        <Tooltip title={intl.formatMessage({ id: 'download.excel' })}>
                                            <span>
                                                <Button
                                                    className={style.excelBtn}
                                                    disabled={isExporting}
                                                    onClick={exportToExcel}>
                                                    {isExporting ? <CircularProgress size={20} /> : <ExcelIcon />}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </div>
                                </Grid>
                                <ExcelExportStatus
                                    exportError={exportError}
                                    isExporting={isExporting}
                                    showProgressBar={false}
                                    exportProgress={null} />
                                {isLoading ? <Loader></Loader> :
                                    <Grid container direction="column" className={style.excludeWhenPrinting} >
                                        <Grid container>

                                            <TableContainer>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>
                                                                <Label>{intl.formatMessage({ id: 'search.individualId' })}</Label>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Label>{intl.formatMessage({ id: 'common.individualReference' })}</Label>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {filteredRows.map((row) => (
                                                            <TableRow className={style.archivedOrdersTableCell} key={row.orderId} onClick={() => handleOnRowClick(row)}>
                                                                <TableCell>{row.controlPerson}</TableCell>
                                                                <TableCell>{row.individualReference}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                                <TablePagination
                                                    count={totalRowCount}
                                                    rowsPerPage={rowsPerPage}
                                                    page={currentPageIndex}
                                                    rowsPerPageOptions={[]}
                                                    onPageChange={onChangePage}
                                                />
                                            </TableContainer>
                                        </Grid>
                                    </Grid>
                                }
                            </Grid>
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <img src={bkflogo} className={'ReadReport__BKFlogo'} style={{ width: '200px', marginRight: '-40px' }} />
                            </div>
                        </div>
                    </Grid>
                }

            </div>
        </>
    )
}
export default ArchivedGroupOrder

type ArchivedGroupOrderSpecificationParams = {
    specification: IArchivedOrderSpecification
}

const useOrderSpecificationStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1em',
        marginBottom: '4em',
        width: '100%'
    },
    headerSpacing: {
        marginBottom: '0.5em'
    },
    rowSpacing: {
        marginBottom: '1.5em'
    },
    summary: {
        paddingTop: '5em',
        marginBottom: '123px', //same spacing as for single reports
        '@media print': {
            display: 'none'
        }
    },
    specificationSpacing: {
        marginBottom: '1em',
    }
})

const ArchivedGroupOrderSpecification = ({ specification }: ArchivedGroupOrderSpecificationParams) => {
    if (!specification) {
        return null
    }

    const styles = useOrderSpecificationStyles()
    const intl = useIntl()
    return <div className={styles.container}>
        <div className={styles.summary}>
            <Heading4 className={styles.headerSpacing}>
                {intl.formatMessage({ id: 'readreport.label.reportSummary' })}
            </Heading4>
            <Paragraph>
                {specification.summary || intl.formatMessage({ id: 'defaultValue.reportSummary' })}
            </Paragraph>
        </div>
        <div>
            <Heading4 className={styles.headerSpacing}>
                {intl.formatMessage({ id: 'readreport.label.orderspecifications' })}
            </Heading4>
            <ReportTable className={`ReadReport__Table TwoColTable ${styles.specificationSpacing}`}>
                <TwoColRow
                    label={intl.formatMessage({ id: 'archived.report.label.report-count' })}
                    value={intl.formatNumber(specification.reportCount)}
                />
                <TwoColRow
                    label={intl.formatMessage({ id: 'archived.report.label.report-discepancy-count' })}
                    value={intl.formatNumber(specification.reportWithDiscrepancyCount)}
                />
                <TwoColRow
                    label={intl.formatMessage({ id: 'readreport.label.ordering.date' })}
                    value={`${intl.formatDate(specification.createdUtc)}`}
                />
                <TwoColRow
                    label={intl.formatMessage({ id: 'archived.report.lastApproval' })}
                    value={specification.approvedUtc
                        ? `${intl.formatDate(specification.approvedUtc)}`
                        : '-'}
                />
                <TwoColRow
                    className={styles.rowSpacing}
                    label={intl.formatMessage({ id: 'readreport.label.delivered' })}
                    value={specification.deliveredUtc
                        ? `${intl.formatDate(specification.deliveredUtc)}`
                        : '-'}
                />
                <TwoColRow
                    label={intl.formatMessage({ id: 'readreport.label.customer' })}
                    value={specification.companyName}
                />
                <TwoColRow
                    label={intl.formatMessage({ id: 'readreport.label.client' })}
                    value={specification.ordererFullName}
                />
                <TwoColRow
                    label={intl.formatMessage({ id: 'readreport.label.reportpackage' })}
                    value={specification.reportPackageName}
                />
                {specification.reportRecipients.map((recipient, index) => (
                    <TwoColRow
                        key={index}
                        label={!index && intl.formatMessage({ id: 'archived.report.label.report-recipients' })}
                        value={recipient.fullName}
                    />
                ))}
            </ReportTable>
            <ExpandingSection
                caption={intl.formatMessage({ id: 'readreport.label.controlpoints' })}>
                <ArchivedCheckpoints checkpoints={specification.checkpoints.map(x => ({ checkpointType: x.type, category: x.category }))} />
            </ExpandingSection>
        </div>
    </div>
}
